import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TreeNode } from 'primeng/api';
import { Category } from './data.service';

@Injectable({
  providedIn: 'root',
})
export class CategoryFilterStateService {
  private categories: Category[] = [];
  private nodeIndex = new Map<string, TreeNode>();

  private treeNodesSubject = new BehaviorSubject<TreeNode[]>([]);
  treeNodes$ = this.treeNodesSubject.asObservable();

  private selectedNodesSubject = new BehaviorSubject<TreeNode[]>([]);
  selectedNodes$ = this.selectedNodesSubject.asObservable();

  private selectedKeysSubject = new BehaviorSubject<Record<string, boolean>>({});
  selectedKeys$ = this.selectedKeysSubject.asObservable();

  private filterCategoriesSubject = new BehaviorSubject<string[]>([]);
  filterCategories$ = this.filterCategoriesSubject.asObservable();

  setCategories(categories: Category[]) {
    this.categories = categories ?? [];
    const { nodes, index } = this.buildCategoryTree(this.categories);
    this.nodeIndex = index;
    this.treeNodesSubject.next(nodes);
    this.syncSelectionFromFilter();
  }

  setFilterCategories(ids: string[] | undefined) {
    const normalized = Array.isArray(ids) ? ids : [];
    if (this.areArraysEqual(normalized, this.filterCategoriesSubject.value)) {
      return;
    }
    this.filterCategoriesSubject.next(normalized);
    this.syncSelectionFromFilter();
  }

  updateFromSelectedNodes(nodes: TreeNode[]) {
    const selectedIds = this.collectKeysWithDescendants(nodes);
    const nextFilter =
      selectedIds.length > 0 ? selectedIds : this.categories.map((category) => category._id);
    if (!this.areArraysEqual(nextFilter, this.filterCategoriesSubject.value)) {
      this.filterCategoriesSubject.next(nextFilter);
    }
    this.selectedNodesSubject.next(nodes);
    this.selectedKeysSubject.next(this.nodesToKeys(nodes));
  }

  updateFromSelectedKeys(keys: Record<string, boolean | { checked?: boolean; partialChecked?: boolean }>) {
    const selectedKeys = Object.entries(keys)
      .filter(([, value]) => {
        if (value === true) return true;
        if (!value || typeof value !== 'object') return false;
        return Boolean((value as { checked?: boolean }).checked);
      })
      .map(([key]) => key);
    const selectedNodes = selectedKeys
      .map((key) => this.nodeIndex.get(key))
      .filter((node): node is TreeNode => !!node);
    const normalizedKeys = selectedKeys.reduce<Record<string, boolean>>((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    this.selectedKeysSubject.next(normalizedKeys);
    this.updateFromSelectedNodes(selectedNodes);
  }

  private syncSelectionFromFilter() {
    const filter = this.filterCategoriesSubject.value;
    if (!filter.length || !this.nodeIndex.size) {
      this.selectedNodesSubject.next([]);
      this.selectedKeysSubject.next({});
      return;
    }
    if (filter.length === this.categories.length) {
      this.selectedNodesSubject.next([]);
      this.selectedKeysSubject.next({});
      return;
    }
    const selectedNodes = filter
      .map((id) => this.nodeIndex.get(id))
      .filter((node): node is TreeNode => !!node);
    this.selectedNodesSubject.next(selectedNodes);
    this.selectedKeysSubject.next(this.nodesToKeys(selectedNodes));
  }

  private collectKeysWithDescendants(nodes: TreeNode[]): string[] {
    const collected = new Set<string>();
    const visit = (node: TreeNode | undefined) => {
      if (!node?.key) return;
      collected.add(String(node.key));
      if (node.children?.length) {
        node.children.forEach((child) => visit(child));
      }
    };
    nodes.forEach((node) => visit(node));
    return Array.from(collected);
  }

  private nodesToKeys(nodes: TreeNode[]): Record<string, boolean> {
    const keys: Record<string, boolean> = {};
    const visit = (node: TreeNode | undefined) => {
      if (!node?.key) return;
      keys[String(node.key)] = true;
      if (node.children?.length) {
        node.children.forEach((child) => visit(child));
      }
    };
    nodes.forEach((node) => visit(node));
    return keys;
  }

  private buildCategoryTree(categories: Category[]): { nodes: TreeNode[]; index: Map<string, TreeNode> } {
    const nodeMap = new Map<string, TreeNode>();
    const tree: TreeNode[] = [];

    for (const category of categories) {
      const node: TreeNode = {
        key: category._id,
        label: category.Name,
        data: category,
      };
      nodeMap.set(category._id, node);
    }

    for (const category of categories) {
      const node = nodeMap.get(category._id);
      if (!node) continue;
      if (category.Parent) {
        const parentNode = nodeMap.get(category.Parent);
        if (parentNode) {
          parentNode.children ??= [];
          parentNode.children.push(node);
        }
      } else {
        tree.push(node);
      }
    }

    for (const node of nodeMap.values()) {
      if (node.children?.length) {
        node.children.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' }));
      } else {
        node.leaf = true;
      }
    }

    tree.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' }));

    return { nodes: tree, index: nodeMap };
  }

  private areArraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const aSorted = [...a].sort();
    const bSorted = [...b].sort();
    return aSorted.every((value, index) => value === bSorted[index]);
  }
}
